import { useEffect, useState, useRef } from "react";
import { useRoom } from "../room/useRoom";
import type { TransferRequest, TransferStatus } from "../core/bus";
import type { RoomPeer } from "../room/peer-state";
import { ChatDrawer, type Message } from "./ChatDrawer";

type Toast = { id: string; message: string };
type PendingRequest = { peerId: string; request: TransferRequest };
type ProgressState = { peerId: string; progress: number; status: TransferStatus } | null;

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export function InteractionManager() {
  const { bus, peers, roomId } = useRoom();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const [progress, setProgress] = useState<ProgressState>(null);

  // Chat History Drawer states
  const [activePeerId, setActivePeerId] = useState<string | null>(null);
  const [activePeerName, setActivePeerName] = useState<string>("");
  const [activePeerDevice, setActivePeerDevice] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  // Refs to avoid stale closures in event listeners
  const peersRef = useRef(peers);
  const activePeerIdRef = useRef(activePeerId);

  useEffect(() => {
    peersRef.current = peers;
  }, [peers]);

  useEffect(() => {
    activePeerIdRef.current = activePeerId;
  }, [activePeerId]);

  // Derived current peer details
  const activePeer = peers.find((p) => p.peerId === activePeerId);
  const currentPeerName = activePeer?.displayName || activePeerName;
  const currentPeerDevice = activePeer?.deviceName || activePeerDevice;

  // Emit unread counts changes to the bus
  useEffect(() => {
    bus.emit("unread-counts-updated", unreadCounts);
  }, [bus, unreadCounts]);

  // Garbage collect unread counts when peers disconnect
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUnreadCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id in next) {
        if (!peers.some((p) => p.peerId === id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [peers]);

  const getPeerDisplayName = (peerId: string) => {
    const peer = peers.find((p: RoomPeer) => p.peerId === peerId);
    return peer?.displayName || peer?.deviceName || peerId;
  };

  const getStorageKey = (peerName: string, peerDevice: string) => {
    return `pairdrop_chat_history_${roomId || "lobby"}_${peerName || "unknown"}_${peerDevice || "unknown"}`;
  };

  const getHistory = (peerName: string, peerDevice: string): Message[] => {
    const key = getStorageKey(peerName, peerDevice);
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error("Failed to load chat history", e);
      return [];
    }
  };

  const saveHistory = (peerName: string, peerDevice: string, history: Message[]) => {
    const key = getStorageKey(peerName, peerDevice);
    try {
      const limitedHistory = history.slice(-100);
      localStorage.setItem(key, JSON.stringify(limitedHistory));
    } catch (e) {
      console.error("Failed to save chat history", e);
    }
  };

  useEffect(() => {
    const showToast = (message: string) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    };

    const handleFilesReceived = (data: { peerId: string; files: File[] }) => {
      showToast(`Received ${data.files.length} file(s)`);
      data.files.forEach((file) => {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      });
      setProgress(null);
    };

    const offs = [
      bus.on("notify-user", (msg: string) => showToast(msg)),
      bus.on("set-progress", (p: { peerId: string; progress: number; status: TransferStatus }) => {
        if (p.progress >= 1 && p.status === "transfer") {
          setProgress(p);
          setTimeout(() => setProgress((curr) => curr && curr.peerId === p.peerId && curr.progress >= 1 ? null : curr), 1500);
        } else if (p.progress === 0 && p.status === "process") {
          setProgress(p);
        } else if (p.progress === 0 && p.status === "prepare") {
          setProgress(p);
        } else {
          setProgress(p);
        }
      }),
      bus.on("files-transfer-request", (d: { request: TransferRequest; peerId: string }) => {
        setPendingRequest({ peerId: d.peerId, request: d.request });
      }),
      bus.on("text-received", (d: { text: string; peerId: string }) => {
        const currentPeers = peersRef.current;
        const senderPeer = currentPeers.find((p) => p.peerId === d.peerId);
        const pName = senderPeer?.displayName || d.peerId;
        const pDevice = senderPeer?.deviceName || "unknown";

        const history = getHistory(pName, pDevice);
        const newMsg: Message = {
          text: d.text,
          sender: "received",
          timestamp: Date.now(),
        };
        history.push(newMsg);
        saveHistory(pName, pDevice, history);

        if (activePeerIdRef.current === d.peerId) {
          setMessages(history);
        } else {
          setUnreadCounts((prev) => {
            const next = { ...prev, [d.peerId]: (prev[d.peerId] || 0) + 1 };
            return next;
          });
          showToast(`New message from ${pName}`);
        }
      }),
      bus.on("ui-request-send-text", (d: { to: string; displayName: string }) => {
        const currentPeers = peersRef.current;
        const targetPeer = currentPeers.find((p) => p.peerId === d.to);
        const pName = targetPeer?.displayName || d.displayName;
        const pDevice = targetPeer?.deviceName || "unknown";

        setActivePeerId(d.to);
        setActivePeerName(pName);
        setActivePeerDevice(pDevice);
        setUnreadCounts((prev) => {
          if (prev[d.to]) {
            return { ...prev, [d.to]: 0 };
          }
          return prev;
        });

        const history = getHistory(pName, pDevice);
        setMessages(history);
      }),
      bus.on("files-received", (d: { peerId: string; files: File[]; imagesOnly: boolean; totalSize: number }) => handleFilesReceived(d)),
      bus.on("file-transfer-cancelled", (d: { peerId: string }) => {
        setProgress((curr) => curr && curr.peerId === d.peerId ? null : curr);
      }),
      bus.on("cancel-files-transfer-request", (d: { peerId: string }) => {
        setPendingRequest((prev) => prev?.peerId === d.peerId ? null : prev);
      }),
      bus.on("peer-disconnected", (peerId: string) => {
        setProgress((curr) => curr && curr.peerId === peerId ? null : curr);
        setPendingRequest((prev) => prev?.peerId === peerId ? null : prev);
        if (activePeerIdRef.current === peerId) {
          setActivePeerId(null);
          showToast("Selected peer left");
        }
      }),
    ];

    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus]);

  const acceptFiles = () => {
    if (!pendingRequest) return;
    bus.emit("respond-to-files-transfer-request", { to: pendingRequest.peerId, accepted: true });
    setPendingRequest(null);
  };

  const declineFiles = () => {
    if (!pendingRequest) return;
    bus.emit("respond-to-files-transfer-request", { to: pendingRequest.peerId, accepted: false });
    setPendingRequest(null);
  };

  const handleCancelTransfer = () => {
    if (!progress) return;
    bus.emit("cancel-file-transfer", { to: progress.peerId });
  };

  const handleSendMessage = (text: string) => {
    if (!activePeerId) return;

    bus.emit("send-text", { to: activePeerId, text });

    const history = getHistory(currentPeerName, currentPeerDevice);
    const newMsg: Message = {
      text,
      sender: "sent",
      timestamp: Date.now(),
    };
    history.push(newMsg);
    saveHistory(currentPeerName, currentPeerDevice, history);
    setMessages(history);
  };

  const handleClearHistory = () => {
    if (window.confirm(`Clear all chat history with ${currentPeerName}?`)) {
      const key = getStorageKey(currentPeerName, currentPeerDevice);
      localStorage.removeItem(key);
      setMessages([]);
    }
  };

  const handleCloseChat = () => {
    setActivePeerId(null);
  };

  return (
    <>
      {/* Toast Notifications */}
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white shadow-lg transition-all animate-bounce"
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Incoming File Request Prompt Overlay */}
      {pendingRequest && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-2xl">
            <h3 className="text-lg font-semibold">Incoming File Transfer</h3>
            <p className="mt-1 text-sm text-[var(--muted)]">
              From <span className="font-semibold text-[var(--text)]">{getPeerDisplayName(pendingRequest.peerId)}</span>
            </p>

            <div className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg)] p-3">
              {pendingRequest.request.thumbnailDataUrl ? (
                <img
                  src={pendingRequest.request.thumbnailDataUrl}
                  alt="Thumbnail"
                  className="h-28 w-28 rounded object-cover shadow"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded bg-[var(--border)] text-[var(--muted)]">
                  📁
                </div>
              )}
              <div className="text-center">
                <p className="text-sm font-medium">
                  {pendingRequest.request.header.length === 1
                    ? pendingRequest.request.header[0].name
                    : `${pendingRequest.request.header.length} files`}
                </p>
                <p className="text-xs text-[var(--muted)]">
                  Total Size: {formatBytes(pendingRequest.request.totalSize)}
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={declineFiles}
                className="flex-1 rounded-lg border border-[var(--border)] py-2 text-sm font-medium transition-colors hover:bg-red-500/10 hover:text-red-500"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={acceptFiles}
                className="flex-1 rounded-lg bg-[var(--color-accent)] py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-strong)]"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar Overlay */}
      {progress && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-xs rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 text-center shadow-2xl">
            <h3 className="text-sm font-semibold capitalize text-[var(--muted)]">
              {progress.status === "prepare"
                ? "Preparing transfer..."
                : progress.status === "wait"
                ? "Waiting for response..."
                : progress.status === "process"
                ? "Processing files..."
                : "Transferring..."}
            </h3>
            <p className="mt-1 text-xs text-[var(--muted)]">
              Device: {getPeerDisplayName(progress.peerId)}
            </p>

            <div className="mt-4 h-2.5 w-full rounded-full bg-[var(--border)]">
              <div
                className="h-2.5 rounded-full bg-[var(--color-accent)] transition-all duration-100"
                style={{ width: `${Math.round(progress.progress * 100)}%` }}
              />
            </div>
            <p className="mt-2 text-sm font-semibold">
              {Math.round(progress.progress * 100)}%
            </p>
            <button
              type="button"
              onClick={handleCancelTransfer}
              className="mt-4 w-full rounded-lg border border-[var(--border)] py-2 text-sm font-medium transition-colors hover:bg-red-500/10 hover:text-red-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Chat History Drawer */}
      <ChatDrawer
        isOpen={activePeerId !== null}
        peerId={activePeer ? activePeerId : null}
        displayName={currentPeerName}
        deviceName={currentPeerDevice}
        messages={messages}
        onClose={handleCloseChat}
        onSendMessage={handleSendMessage}
        onClearHistory={handleClearHistory}
      />
    </>
  );
}
